import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { OperatorTypes } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import ThresholdRange from '@avtechsoftware/ra-javascript-utilities/src/classes/thresholdranges/ThresholdRange';
import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import AltErrorWrapper
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/AltErrorWrapper/AltErrorWrapper';
import ReadingType, { findReadingTypeById } from '@avtechsoftware/ra-javascript-utilities/src/types/ReadingType';
import createInstanceCopy from '@avtechsoftware/ra-javascript-utilities/src/utilities/ObjectUtil';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';

const RaaThresholdRangeForm = ({
  thresholdRange: initialThresholdRange,
  onSave,
  onCancel,
  errorList,
}) => {
  // Get temperature scale from user preferences
  const { temperatureScale, airSpeedScale } = useSelector(selectSessionInformation);

  const isEditForm = initialThresholdRange.id && initialThresholdRange.id > 0;

  const [thresholdRange, setThresholdRange] = useState(
    () => createInstanceCopy(initialThresholdRange),
  );

  const [units, setUnits] = useState('');

  useEffect(() => {
    if (thresholdRange.readingType !== null && thresholdRange.readingType !== undefined) {
      const readingType = findReadingTypeById(thresholdRange.readingType);
      const currentUnits = readingType.getUnits({
        temperatureScale,
        airSpeedScale,
      });
      setUnits(currentUnits);
    }
  }, [thresholdRange.readingType, temperatureScale, airSpeedScale]);

  const onChange = (field, newValue) => {
    const updatedThresholdRange = Object.assign(
      Object.create(Object.getPrototypeOf(thresholdRange)),
      thresholdRange,
    );

    updatedThresholdRange[field] = newValue;

    setThresholdRange(updatedThresholdRange);
  };

  const handleSave = () => {
    const updatedThresholdRange = Object.assign(
      Object.create(Object.getPrototypeOf(thresholdRange)),
      thresholdRange,
    );

    const readingType = findReadingTypeById(thresholdRange.readingType);

    updatedThresholdRange.value = thresholdRange.value === '' ? null : thresholdRange.value;
    updatedThresholdRange.altValue = thresholdRange.altValue === '' ? null : thresholdRange.altValue;

    updatedThresholdRange.channelTypeId = readingType.channelTypeId;
    onSave(updatedThresholdRange);
  };

  return (
    <div>
      <FormErrorHandler
        fieldList={['name', 'value', 'readingType']}
        errorList={errorList}
      >
        <h2 className="mb-4">Threshold Range Form</h2>
        <div className="row">
          <div className="col-xl-4 col-md-6 col-12">
            <AltErrorWrapper fieldId="name">
              <input
                className="form-control"
                value={thresholdRange.name}
                onChange={(e) => onChange('name', e.target.value)}
              />
            </AltErrorWrapper>
          </div>
        </div>
        <hr />
        <div className="row mb-2">
          <div className="col-xl-4 col-md-6 col-12">
            <AltErrorWrapper fieldId="readingType">
              <select
                className="form-control"
                value={thresholdRange.readingType}
                onChange={(e) => onChange(
                  'readingType', parseInt(e.target.value, 10),
                )}
                disabled={isEditForm}
              >
                <option value={-1} disabled> - Choose Reading Type - </option>
                {Object.values(ReadingType)
                  .filter((rt) => rt.id > 0 && rt.id !== ReadingType.GENERIC_BOOLEAN.id)
                  .map((rt) => (
                    <option key={rt.id} value={rt.id}>
                      {rt.displayName}
                    </option>
                  ))}
              </select>
            </AltErrorWrapper>
          </div>
        </div>
        <div className="row mb-2">
          <div className="col-xl-4 col-md-6 col-12">
            <AltErrorWrapper fieldId="operatorId">
              <select
                className="form-control"
                value={thresholdRange.operatorId}
                onChange={(e) => onChange(
                  'operatorId', parseInt(e.target.value, 10),
                )}
                disabled={isEditForm}
              >
                <option value={-1} disabled> - Choose Operator - </option>
                <option value={1}>is above</option>
                <option value={3}>is below</option>
                <option value={6}>is between</option>
                <option value={7}>is not between</option>
              </select>
            </AltErrorWrapper>
          </div>
        </div>
        {thresholdRange.operatorId === OperatorTypes.BETWEEN
        || thresholdRange.operatorId === OperatorTypes.NOT_BETWEEN
          ? (
            <AltErrorWrapper fieldId="value">
              <div className="row mb-2 form-inline">
                <div className="col-md-auto col-11 my-1">
                  <input
                    className="form-control"
                    value={thresholdRange.value}
                    placeholder="Enter Range Low"
                    onChange={(e) => onChange(
                      'value', e.target.value,
                    )}
                  />
                </div>
                {units}
                <div className="col-md-auto col-11 text-center">
                  and
                </div>
                <div className="col-md-auto col-11 my-1">
                  <input
                    className="form-control"
                    value={thresholdRange.altValue}
                    placeholder="Enter Range High"
                    onChange={(e) => onChange(
                      'altValue', e.target.value,
                    )}
                  />
                </div>
                {units}
              </div>
            </AltErrorWrapper>
          )
          : (
            <AltErrorWrapper fieldId="value">
              <div className="row mb-2 form-inline">
                <div className="col-md-auto col-11">
                  <input
                    className="form-control"
                    value={thresholdRange.value}
                    placeholder="Enter Value"
                    onChange={(e) => onChange(
                      'value', e.target.value,
                    )}
                  />
                </div>
                {units}
              </div>
            </AltErrorWrapper>
          )}

        {(isEditForm && (thresholdRange.alertCount > 0 || thresholdRange.metricsGroupCount > 0))
          && (
            <div className="alert alert-danger d-inline-flex align-items-center mt-3 w-auto">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="mr-2"
              />
              <div>
                This change will immediately be reflected in
                {'  '}
                <b>
                  {thresholdRange.alertCount}
                  {' '}
                  alerts
                </b>
                {' '}
                and
                {' '}
                <b>
                  {thresholdRange.metricsGroupCount}
                  {' '}
                  graphs
                </b>
                {' '}
                associated with this threshold.
              </div>
            </div>
          )}

        <div className="row mt-3">
          <div className="col-auto">
            <button type="button" className="btn btn-primary" onClick={handleSave}>
              Save Changes
            </button>
          </div>
          <div className="col-auto">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </div>
      </FormErrorHandler>
    </div>
  );
};

RaaThresholdRangeForm.propTypes = {
  thresholdRange: PropTypes.shape(ThresholdRange).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  errorList: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string,
    message: PropTypes.string,
  })).isRequired,
};

export { RaaThresholdRangeForm };
export default RaaThresholdRangeForm;
